// Curated feather-style SVG icons for editor-pinned custom RMI shortcuts.
// All icons are authored as inner <svg> path markup to be rendered inside
// a single wrapper that sets viewBox / stroke / fill via `currentColor`.
// Keeping the set small and static avoids any icon-library dependency and
// matches the inline-SVG convention already used by RegNrSearch / ErrorDisplay.

export type ShortcutIconCode =
  | 'wrench'
  | 'engine'
  | 'air-filter'
  | 'oil-filter'
  | 'brake'
  | 'battery'
  | 'bulb'
  | 'belt'
  | 'spark-plug'
  | 'wheel'
  | 'gearbox'
  | 'cooling'
  | 'exhaust'
  | 'suspension'
  | 'document'
  | 'fuel'
  | 'electric'
  | 'gauge'

export interface ShortcutIconDef {
  code: ShortcutIconCode
  label: string
  svgPath: string
}

// Each svgPath is rendered inside:
//   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
//        stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
//     {svgPath}
//   </svg>
export const SHORTCUT_ICONS: ShortcutIconDef[] = [
  {
    code: 'wrench',
    label: 'Verktøy',
    svgPath:
      '<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>',
  },
  {
    code: 'engine',
    label: 'Motor',
    svgPath:
      '<rect x="3" y="7" width="14" height="10" rx="1"/><path d="M7 7V4h6v3"/><path d="M17 10h3v4h-3"/><path d="M3 11h2"/><path d="M3 14h2"/>',
  },
  {
    code: 'air-filter',
    label: 'Luftfilter',
    svgPath:
      '<rect x="3" y="6" width="18" height="12" rx="1"/><path d="M7 6v12"/><path d="M11 6v12"/><path d="M15 6v12"/><path d="M19 6v12"/>',
  },
  {
    code: 'oil-filter',
    label: 'Oljefilter',
    svgPath:
      '<path d="M8 3h8v3H8z"/><path d="M6 6h12v12a3 3 0 0 1-3 3H9a3 3 0 0 1-3-3z"/><path d="M9 12h6"/><path d="M9 16h6"/>',
  },
  {
    code: 'brake',
    label: 'Bremser',
    svgPath:
      '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4"/><path d="M12 3v3"/><path d="M12 18v3"/><path d="M3 12h3"/><path d="M18 12h3"/>',
  },
  {
    code: 'battery',
    label: 'Batteri',
    svgPath:
      '<rect x="2" y="7" width="18" height="11" rx="1"/><path d="M20 11h2v3h-2"/><path d="M6 11v3"/><path d="M10 11v3"/><path d="M14 11v3"/>',
  },
  {
    code: 'bulb',
    label: 'Lys / pære',
    svgPath:
      '<path d="M9 18h6"/><path d="M10 21h4"/><path d="M12 3a6 6 0 0 0-4 10.5c1 1 2 2 2 3.5h4c0-1.5 1-2.5 2-3.5A6 6 0 0 0 12 3z"/>',
  },
  {
    code: 'belt',
    label: 'Reim',
    svgPath:
      '<ellipse cx="7" cy="12" rx="4" ry="4"/><ellipse cx="17" cy="12" rx="4" ry="4"/><path d="M7 8h10"/><path d="M7 16h10"/>',
  },
  {
    code: 'spark-plug',
    label: 'Tennplugg',
    svgPath:
      '<path d="M10 2h4v6h-4z"/><path d="M9 8h6v4H9z"/><path d="M11 12v4"/><path d="M13 12v4"/><path d="M10 16h4v4h-4z"/><path d="M12 20v2"/>',
  },
  {
    code: 'wheel',
    label: 'Hjul / felg',
    svgPath:
      '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3"/><path d="M12 3v6"/><path d="M12 15v6"/><path d="M3 12h6"/><path d="M15 12h6"/><path d="M5.6 5.6l4.2 4.2"/><path d="M14.2 14.2l4.2 4.2"/><path d="M18.4 5.6l-4.2 4.2"/><path d="M9.8 14.2l-4.2 4.2"/>',
  },
  {
    code: 'gearbox',
    label: 'Girkasse',
    svgPath:
      '<circle cx="6" cy="6" r="2"/><circle cx="6" cy="18" r="2"/><circle cx="18" cy="6" r="2"/><circle cx="18" cy="18" r="2"/><path d="M6 8v8"/><path d="M18 8v8"/><path d="M6 6h12"/><path d="M12 6v12"/>',
  },
  {
    code: 'cooling',
    label: 'Kjøling',
    svgPath:
      '<path d="M12 2v20"/><path d="M2 12h20"/><path d="M5 5l14 14"/><path d="M19 5L5 19"/><circle cx="12" cy="12" r="2"/>',
  },
  {
    code: 'exhaust',
    label: 'Eksos',
    svgPath:
      '<path d="M2 14h12a3 3 0 0 0 3-3V9a3 3 0 0 1 3-3h2"/><path d="M18 14h4"/><circle cx="20" cy="14" r="2"/><path d="M4 17c0 1 1 2 2 2"/><path d="M8 17c0 1 1 2 2 2"/>',
  },
  {
    code: 'suspension',
    label: 'Hjuloppheng',
    svgPath:
      '<path d="M6 3v6l-2 2v8"/><path d="M18 3v6l2 2v8"/><path d="M8 11h8"/><path d="M8 14h8"/><path d="M8 17h8"/><circle cx="6" cy="20" r="1"/><circle cx="18" cy="20" r="1"/>',
  },
  {
    code: 'document',
    label: 'Dokument',
    svgPath:
      '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/>',
  },
  {
    code: 'fuel',
    label: 'Drivstoff',
    svgPath:
      '<path d="M3 22h12V4a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2z"/><path d="M15 8h3a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-1"/><path d="M6 8h6"/><path d="M6 12h6"/>',
  },
  {
    code: 'electric',
    label: 'Elektrisk',
    svgPath:
      '<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>',
  },
  {
    code: 'gauge',
    label: 'Sensor / måler',
    svgPath:
      '<path d="M12 14l4-4"/><path d="M3.5 14a9 9 0 1 1 17 0"/><circle cx="12" cy="14" r="1"/>',
  },
]

export const DEFAULT_ICON: ShortcutIconCode = 'wrench'

export function getIcon(code: string | null | undefined): ShortcutIconDef {
  const found = SHORTCUT_ICONS.find(i => i.code === code)
  if (found) return found
  return SHORTCUT_ICONS.find(i => i.code === DEFAULT_ICON)!
}
